import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SiteHeader from '../components/SiteHeader';
import MoveList from '../components/MoveList';
import Tabs from '../components/Tabs';
import SideBadge from '../components/SideBadge';
import TheoryPanel from '../components/TheoryPanel';
import MistakeExplorer from '../components/MistakeExplorer';
import PlanPanel from '../components/PlanPanel';
import DeepenControl from '../components/DeepenControl';
import Chessboard, { type BoardTheme } from '../board/Chessboard';
import { useLineTrainer, type TrainerMode } from '../study/useLineTrainer';
import { useAuth } from '../auth/AuthContext';
import { studyApi } from '../api/studyApi';
import { openingsApi } from '../api/openingsApi';
import {
  LINE_LEVELS,
  type Line,
  type Opening,
  type LineLevel,
  type QueueItem,
  type ReviewGrade,
  type StudyQueue,
  type UserSettings,
} from '../../../shared/types';

/**
 * The training session, in two modes.
 *
 * **Study** walks you through a line with every move and its reasoning shown
 * before you play it. Nothing is scored or scheduled — it is the first pass
 * through material you have not seen.
 *
 * **Practice** tests you from memory and schedules the result.
 *
 * A session is a queue fetched once and played through locally; practice results
 * are posted as each line finishes rather than batched, so closing the tab
 * halfway through never loses the work already done.
 *
 * When no mode is forced by the URL, each item picks its own: a line you have
 * never seen opens in Study and one that has fallen due opens in Practice. Being
 * quizzed on a line before ever being shown it is the one thing the session
 * should never do.
 */

const GRADES: { grade: ReviewGrade; label: string; hint: string; tone: string }[] = [
  { grade: 'again', label: 'Again', hint: 'Could not recall it', tone: 'again' },
  { grade: 'hard', label: 'Hard', hint: 'Got there, but slowly', tone: 'hard' },
  { grade: 'good', label: 'Good', hint: 'Knew it', tone: 'good' },
  { grade: 'easy', label: 'Easy', hint: 'Instant', tone: 'easy' },
];

function defaultModeFor(item: QueueItem): TrainerMode {
  return item.reason === 'new' ? 'study' : 'practice';
}

export default function StudyPage() {
  const { user, getToken, refresh, loading: authLoading } = useAuth();
  const [params, setParams] = useSearchParams();
  const openingId = params.get('opening') ?? undefined;
  // "Practice this variation" narrows a session to one line, so you can drop
  // straight into the variation you were reading instead of hunting for it.
  const lineId = params.get('line') ?? undefined;
  const forcedMode = params.get('mode') === 'study' ? 'study'
    : params.get('mode') === 'practice' ? 'practice'
    : null;
  // Whether this session is limited to the repertoire level or covers every
  // line of every opening added. In the URL rather than in component state so
  // that a reload, a back button and a shared link all keep the same session.
  const scope = params.get('scope') === 'all' ? 'all' : 'level';
  const setScope = (next: 'level' | 'all') => {
    const p = new URLSearchParams(params);
    if (next === 'all') p.set('scope', 'all');
    else p.delete('scope');
    setParams(p);
  };

  const auth = useMemo(() => ({ getToken, refresh }), [getToken, refresh]);

  const [queue, setQueue] = useState<StudyQueue | null>(null);
  const [settings, setSettings] = useState<UserSettings | null>(null);
  // The full opening, for its theory. The queue carries only enough context to
  // label a line, but the strategic writeup is exactly what you want *while*
  // studying rather than on a different page -- it is cached per tab, so
  // flicking between lines of the same opening costs nothing.
  const [openingDetail, setOpeningDetail] = useState<Opening | null>(null);
  const [index, setIndex] = useState(0);
  const [mode, setMode] = useState<TrainerMode>(forcedMode ?? 'practice');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [practiced, setDrilled] = useState(0);
  const [studied, setStudied] = useState(0);
  const [flipped, setFlipped] = useState(false);

  useEffect(() => {
    if (authLoading || !user) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      // A study session asks for the whole scope, not just what is due.
      studyApi.queue(auth, { openingId, lineId, mode: forcedMode ?? undefined, scope }),
      studyApi.settings(auth),
    ])
      .then(([q, s]) => {
        if (cancelled) return;
        setQueue(q);
        setSettings(s);
        setIndex(0);
        setDrilled(0);
        setStudied(0);
        if (q.items.length) setMode(forcedMode ?? defaultModeFor(q.items[0]));
      })
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : 'Failed to load your queue'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [auth, openingId, lineId, forcedMode, scope, user, authLoading]);

  /**
   * Refetch the queue after the scope has changed underneath it.
   *
   * Unlocking a level mid-session adds lines to the very list being walked, so
   * the session has to be re-read rather than patched. `stayOn` keeps the line
   * that is on the board in front of the user: without it, adding six
   * variations would silently throw them back to the first line of the session,
   * which is the same class of bug as the counter that read "5 / 4".
   */
  const reloadQueue = useCallback(
    async (stayOn?: string) => {
      const q = await studyApi.queue(auth, { openingId, lineId, mode: forcedMode ?? undefined, scope });
      setQueue(q);
      if (stayOn) {
        const again = q.items.findIndex((it) => it.line.id === stayOn);
        if (again >= 0) setIndex(again);
      }
    },
    [auth, openingId, lineId, forcedMode, scope],
  );

  // Which levels this session is showing. Session-local: narrowing here changes
  // what you see now, not what your repertoire schedules.
  const [shownLevels, setShownLevels] = useState<LineLevel[]>([...LINE_LEVELS]);

  const levelsPresent = useMemo(
    () => LINE_LEVELS.filter((lv) => (queue?.items ?? []).some((q) => q.line.level === lv)),
    [queue],
  );

  // Easiest first, then by length, so a session builds rather than lurching
  // between a three-move beginner line and twenty plies of Najdorf theory.
  const visible = useMemo(() => {
    const items = (queue?.items ?? []).filter((q) => shownLevels.includes(q.line.level));
    return [...items].sort(
      (a, b) =>
        LINE_LEVELS.indexOf(a.line.level) - LINE_LEVELS.indexOf(b.line.level) ||
        a.line.moves.length - b.line.moves.length ||
        a.line.name.localeCompare(b.line.name),
    );
  }, [queue, shownLevels]);

  const visibleByLevel = useMemo(() => {
    const out: Partial<Record<LineLevel, QueueItem[]>> = {};
    visible.forEach((q) => (out[q.line.level] ??= []).push(q));
    return out;
  }, [visible]);

  // A filter change can leave the cursor past the end of the shortened list.
  useEffect(() => {
    setIndex((i) => (i < visible.length ? i : 0));
  }, [visible.length]);

  // No wrap-around fallback. `visible[index] ?? visible[0]` looked harmless and
  // meant that stepping past the last line silently restarted the session at the
  // first one while the counter kept climbing -- hence "5 / 4". Running off the
  // end yields null, which is the session-complete state.
  const item: QueueItem | null = visible[index] ?? null;
  const onLastLine = visible.length > 0 && index >= visible.length - 1;

  // Load the opening behind whichever line is on screen. Cached in openingsApi,
  // so moving between lines of the same opening does not refetch.
  useEffect(() => {
    const id = item?.opening.id;
    if (!id || openingDetail?.id === id) return;
    let cancelled = false;
    openingsApi
      .get(id)
      .then((o) => !cancelled && setOpeningDetail(o))
      .catch(() => !cancelled && setOpeningDetail(null));
    return () => {
      cancelled = true;
    };
  }, [item?.opening.id, openingDetail?.id]);
  const trainer = useLineTrainer(item?.line ?? null, settings?.opponentDelayMs ?? 350, mode);

  // Advances through the *filtered* list, so hiding a level really removes it
  // from the session rather than just from the dropdown.
  const goToNext = useCallback(() => {
    setIndex((i) => {
      const next = i + 1;
      const nextItem = visible[next];
      if (nextItem) setMode(forcedMode ?? defaultModeFor(nextItem));
      return next;
    });
  }, [visible, forcedMode]);

  const submit = useCallback(
    async (grade: ReviewGrade) => {
      if (!item || submitting) return;
      setSubmitting(true);
      try {
        await studyApi.review(
          {
            lineId: item.line.id,
            grade,
            movesCorrect: trainer.movesCorrect,
            movesTotal: trainer.movesTotal,
          },
          auth,
        );
        setDrilled((n) => n + 1);
        goToNext();
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not save that review');
      } finally {
        setSubmitting(false);
      }
    },
    [item, submitting, trainer.movesCorrect, trainer.movesTotal, auth, goToNext],
  );

  // Grade with the number keys — a session is dozens of lines, and reaching
  // for the mouse every time is the difference between five minutes and fifteen.
  useEffect(() => {
    if (mode !== 'practice' || !trainer.finished || submitting) return;
    function onKey(e: KeyboardEvent) {
      const found = GRADES[Number(e.key) - 1];
      if (found) {
        e.preventDefault();
        void submit(found.grade);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mode, trainer.finished, submitting, submit]);

  // In study mode the right arrow plays the move being taught, so a line can be
  // read through one-handed.
  useEffect(() => {
    if (mode !== 'study') return;
    function onKey(e: KeyboardEvent) {
      const target = e.target as HTMLElement | null;
      if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;
      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        trainer.advance();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mode, trainer]);

  if (authLoading) return <Shell><p className="muted">Loading…</p></Shell>;

  if (!user) {
    return (
      <Shell>
        <div className="empty-state">
          <h1>Sign in to study</h1>
          <p className="muted">
            Your repertoire and review schedule are stored against your account, so studying needs
            you to be signed in. Browsing openings and reading theory does not.
          </p>
          <div className="empty-actions">
            <Link className="btn btn--primary" to="/login" state={{ from: '/study' }}>
              Sign in
            </Link>
            <Link className="btn" to="/openings">
              Browse openings
            </Link>
          </div>
        </div>
      </Shell>
    );
  }

  if (loading) return <Shell><p className="muted">Loading your queue…</p></Shell>;

  if (error && !queue) {
    return <Shell><p className="error">{error}</p></Shell>;
  }

  if (!item) {
    const didSomething = practiced + studied > 0;
    return (
      <Shell>
        <div className="empty-state">
          <h1>{didSomething ? 'Session complete' : 'Nothing due right now'}</h1>
          {didSomething ? (
            <p className="muted">
              {studied > 0 && `You studied ${studied} ${studied === 1 ? 'line' : 'lines'}`}
              {studied > 0 && practiced > 0 && ' and '}
              {practiced > 0 && `you practiced ${practiced} ${practiced === 1 ? 'line' : 'lines'}`}
              {practiced > 0
                ? '. Lines you practiced are scheduled to come back just before you would forget them.'
                : '. Studying schedules nothing — practice a line when you want it added to your reviews.'}
            </p>
          ) : (
            <p className="muted">
              {queue && queue.dueCount === 0 && queue.newCount === 0
                ? 'Add an opening to your repertoire to start building a review schedule.'
                : 'Everything in your repertoire is up to date.'}
            </p>
          )}
          <div className="empty-actions">
            {/* Studying schedules nothing, so after a study session the useful
                next step is practising what you just read, not going home. */}
            {studied > 0 && (
              <Link
                className="btn btn--primary"
                to={`/study?${openingId ? `opening=${openingId}&` : ''}mode=practice`}
              >
                Practice {studied === 1 ? 'it' : 'them'} now
              </Link>
            )}
            <Link className={`btn${studied > 0 ? '' : ' btn--primary'}`} to="/">
              Back to today
            </Link>
            <Link className="btn" to="/openings">Find an opening</Link>
          </div>
        </div>
      </Shell>
    );
  }

  const { line, opening, reason } = item;
  const orientation = flipped
    ? line.side === 'white' ? 'black' : 'white'
    : (line.side as 'white' | 'black');

  return (
    <Shell>
      <div className="study-top">
        <div>
          <div className="study-crumbs">
            <Link to={`/openings/${opening.id}`}>{opening.name}</Link>
            <span className="crumb-sep">›</span>
            <span>{line.name}</span>
          </div>
          <div className="study-tags">
            <SideBadge side={line.side} />
            <span className="tag">{line.eco}</span>
            <span className={`tag tag--${reason}`}>
              {reason === 'new' ? 'Not seen yet' : reason === 'due' ? 'Due for review' : 'Ahead of schedule'}
            </span>
          </div>
        </div>

        <div className="study-top-right">
          <ModeToggle mode={mode} onChange={setMode} />
          {/* A session was a one-way conveyor: you took what you were given in
              the order it came, with no indication of how hard anything was.
              Now it is ordered easiest-first and every entry names its level. */}
          <label className="session-jump">
            <span className="sr-only">Jump to a variation</span>
            <select
              value={item.line.id}
              onChange={(e) => {
                const next = visible.findIndex((q) => q.line.id === e.target.value);
                if (next >= 0) setIndex(next);
                setFlipped(false);
              }}
              aria-label="Jump to a variation in this session"
            >
              {LINE_LEVELS.filter((lv) => visibleByLevel[lv]?.length).map((lv) => (
                <optgroup key={lv} label={`${lv} (${visibleByLevel[lv]!.length})`}>
                  {visibleByLevel[lv]!.map((qi) => (
                    <option key={qi.line.id} value={qi.line.id}>
                      {qi.line.name} · {Math.ceil(qi.line.moves.length / 2)} moves
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </label>
          <span className="study-progress-count">
            {index + 1} / {visible.length}
          </span>
        </div>
      </div>

      {/* Filter the session by level. The queue is what your repertoire says you
          should see; this narrows it for one sitting without changing anything
          stored, which is the difference between "today I only want the sharp
          lines" and "stop scheduling the easy ones". */}
      {levelsPresent.length > 1 && (
        <div className="session-filter">
          <span className="session-filter-label">Show</span>
          {levelsPresent.map((lv) => {
            const on = shownLevels.includes(lv);
            const n = (queue?.items ?? []).filter((q) => q.line.level === lv).length;
            return (
              <button
                key={lv}
                type="button"
                className={`band band--small${on ? ' band--on' : ''}`}
                aria-pressed={on}
                disabled={on && shownLevels.length === 1}
                onClick={() =>
                  setShownLevels(on ? shownLevels.filter((l) => l !== lv) : [...shownLevels, lv])
                }
                title={
                  on && shownLevels.length === 1
                    ? 'Keep at least one level visible'
                    : `${on ? 'Hide' : 'Show'} ${lv} variations`
                }
              >
                <span className={`level-dot level-dot--${lv}`} aria-hidden="true" />
                {lv}
                <span className="band-count">{n}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* What this session actually covers, and what it does not.
          The repertoire level exists to meter *scheduling* -- everything you
          practice today comes back tomorrow -- but it was also silently
          deciding how much you were allowed to look at, and at the default
          level most openings expose one or two variations. A session could be
          seven lines out of thirty-one with nothing on screen accounting for
          the other twenty-four, which reads as an app with no content rather
          than as a deliberately narrow queue. */}
      {queue && !lineId && (queue.beyondLevel > 0 || scope === 'all') && (
        <p className="session-scope">
          {scope === 'all' ? (
            <>
              Showing <strong>every variation</strong> in your repertoire —{' '}
              {queue.totalInRepertoire} in total.{' '}
              <button type="button" className="linkish" onClick={() => setScope('level')}>
                Back to the suggested set
              </button>
            </>
          ) : (
            <>
              {mode === 'study' ? 'Studying' : 'Practising'}{' '}
              <strong>{queue.items.length}</strong> of {queue.totalInRepertoire} variations in your
              repertoire. The other {queue.beyondLevel} are above the level you have each opening
              set to — {mode === 'study' ? 'studying' : 'practising'} them costs nothing extra to
              look at.{' '}
              <button type="button" className="linkish" onClick={() => setScope('all')}>
                {mode === 'study' ? 'Study' : 'Practise'} all {queue.totalInRepertoire}
              </button>
            </>
          )}
        </p>
      )}

      {/* Raise the level without leaving the session.
          Running out of lines mid-session used to mean going back to the
          opening page to find the level control, which is a long way round for
          a decision you have only just realised you want to make. The advice
          shown here is the pathway's, computed server-side, so a button greyed
          on one screen is greyed on the other for the same stated reason. */}
      {item && queue?.depth?.[item.opening.id]?.nextLevel && (
        <div className="session-deepen">
          <span className="session-deepen-label">
            {item.opening.name} · {queue.depth[item.opening.id].currentLevel}
          </span>
          <DeepenControl
            openingId={item.opening.id}
            name={item.opening.name}
            depth={queue.depth[item.opening.id]}
            auth={auth}
            onChanged={() => reloadQueue(item.line.id)}
            compact
          />
        </div>
      )}

      {queue && queue.newBeyondPace > 0 && (
        <p className="session-note muted">
          This session includes <strong>{queue.newBeyondPace}</strong> new{' '}
          {queue.newBeyondPace === 1 ? 'variation' : 'variations'} beyond your suggested
          pace of {queue.newPerDay} a day. Nothing is held back — just be aware that
          everything you learn today returns for review tomorrow.{' '}
          <Link to="/profile">Adjust the suggestion</Link>.
        </p>
      )}

      <div className="study-layout">
        <div className="study-board">
          <Chessboard
            fen={trainer.fen}
            orientation={orientation}
            onMove={trainer.play}
            disabled={!trainer.isTraineeTurn || trainer.finished}
            lastMove={trainer.lastMove}
            hintMove={trainer.hintMove}
            theme={(settings?.boardTheme as BoardTheme) ?? 'walnut'}
            showCoordinates={settings?.showCoordinates ?? true}
          />
          <div className="board-controls">
            {mode === 'study' && (
              <>
                {/* Going back was missing entirely, so re-reading the reasoning
                    for a move you had passed meant restarting the line. */}
                <button
                  type="button"
                  className="btn btn--small"
                  onClick={trainer.back}
                  disabled={trainer.ply === 0}
                >
                  ← Back
                </button>
                <button
                  type="button"
                  className="btn btn--small btn--primary"
                  onClick={trainer.advance}
                  disabled={!trainer.isTraineeTurn || trainer.finished}
                >
                  Next →
                </button>
              </>
            )}
            <button type="button" className="btn btn--small" onClick={() => setFlipped((f) => !f)}>
              Flip board
            </button>
            <button
              type="button"
              className="btn btn--small"
              onClick={trainer.restart}
              disabled={trainer.ply === 0}
            >
              Restart line
            </button>
            {mode === 'practice' && (
              <button
                type="button"
                className="btn btn--small"
                onClick={trainer.reveal}
                disabled={!trainer.isTraineeTurn}
              >
                Show me
              </button>
            )}

            {/* What to do when a study line ends. These were a card of their own
                in the side column, which was a whole block of chrome for two
                buttons that belong with every other board action. */}
            {trainer.finished && mode === 'study' && (
              /* Marked rather than scrolled to. The page used to jump on
                 completion with no indication of what it had jumped to, which
                 is worse than not moving at all -- the answer is to make the
                 thing obvious where it already is. */
              <div className="board-controls-done">
                <span className="board-controls-sep" aria-hidden="true" />
                <span className="done-flag">Line complete</span>
                <button
                  type="button"
                  className="btn btn--small"
                  onClick={() => setMode('practice')}
                  title="Studying schedules nothing — practice it to add it to your reviews"
                >
                  Practice it
                </button>
                <button
                  type="button"
                  className="btn btn--small btn--primary"
                  onClick={() => {
                    setStudied((n) => n + 1);
                    goToNext();
                  }}
                >
                  {onLastLine ? 'Finish study' : 'Next line →'}
                </button>
              </div>
            )}
          </div>
        </div>

        <aside className="study-side">
          {/* When a line ends, what to do next is the whole point of the
              screen -- so it goes at the top rather than below everything you
              have just finished reading. */}
          {trainer.finished && mode === 'practice' && (
            <div className="grade-card">
              <h3 className="grade-title">How did that go?</h3>
              <p className="muted grade-accuracy">
                {trainer.movesCorrect} of {trainer.movesTotal} moves right first time.
              </p>
              <div className="grade-buttons">
                {GRADES.map((g, i) => (
                  <button
                    key={g.grade}
                    type="button"
                    className={`grade-btn grade-btn--${g.tone}`}
                    onClick={() => void submit(g.grade)}
                    disabled={submitting}
                  >
                    <span className="grade-key">{i + 1}</span>
                    <span className="grade-label">{g.label}</span>
                    <span className="grade-hint">{g.hint}</span>
                  </button>
                ))}
              </div>
              {error && <p className="error">{error}</p>}
            </div>
          )}

          {/* The coach stays put: it is what you are reading right now, and it
              is short. Everything you only dip into is behind tabs, which is
              what keeps this column the height of the board rather than three
              times it. */}
          <Coach trainer={trainer} line={line} orientation={orientation} />

          <Tabs
            className="study-tabs"
            tabs={[
              {
                key: 'moves',
                label: 'Moves',
                badge: `${Math.min(trainer.ply, line.moves.length)}/${line.moves.length}`,
                content: (
                  <MoveList
                    line={line}
                    ply={trainer.ply}
                    onSeek={trainer.seekTo}
                    canSeek={trainer.canSeek}
                    trunkPlies={line.trunkPlies}
                  />
                ),
              },
              ...(openingDetail
                ? [
                    {
                      key: 'theory',
                      label: 'Theory',
                      content: (
                        <TheoryPanel
                          theory={openingDetail.theory}
                          position={{
                            fen: openingDetail.fen,
                            side: openingDetail.side,
                            moves: openingDetail.moves,
                          }}
                        />
                      ),
                    },
                  ]
                : []),
              ...(line.plan
                ? [
                    {
                      key: 'plan',
                      label: 'The plan',
                      content: <PlanPanel plan={line.plan} side={line.side} />,
                    },
                  ]
                : []),
            ]}
          />

        </aside>
      </div>
    </Shell>
  );
}

function ModeToggle({ mode, onChange }: { mode: TrainerMode; onChange: (m: TrainerMode) => void }) {
  return (
    <div className="mode-toggle" role="group" aria-label="Training mode">
      <button
        type="button"
        className={`mode-btn${mode === 'study' ? ' mode-btn--active' : ''}`}
        aria-pressed={mode === 'study'}
        onClick={() => onChange('study')}
        title="Be shown each move and why it is played. Nothing is scored."
      >
        Study
      </button>
      <button
        type="button"
        className={`mode-btn${mode === 'practice' ? ' mode-btn--active' : ''}`}
        aria-pressed={mode === 'practice'}
        onClick={() => onChange('practice')}
        title="Find the moves from memory. The result is graded and scheduled."
      >
        Practice
      </button>
    </div>
  );
}

/** The explanation panel — what to play, or why the last move was right or wrong. */
function Coach({
  trainer,
  line,
  orientation,
}: {
  trainer: ReturnType<typeof useLineTrainer>;
  line: Line;
  orientation: 'white' | 'black';
}) {
  // Moves played so far, which is the position a mistake would branch from.
  const prefix = line.moves.slice(0, trainer.ply).map((m) => m.san);

  if (trainer.finished) {
    return (
      <div className="coach coach--done">
        <h3 className="coach-title">Line complete — {line.name}</h3>
        <p>{line.finalComment}</p>
        {/* The plan has its own tab now. Repeating it here is what made the
            completion state tall enough to push everything else off screen. */}
        {line.plan && <p className="muted">Open the Plan tab for what to do from here.</p>}
      </div>
    );
  }

  const { feedback } = trainer;

  if (feedback.kind === 'teaching') {
    return (
      <div className="coach coach--teaching">
        <h3 className="coach-title">
          Play {feedback.san}
          {feedback.move.critical && <span className="coach-key">key move</span>}
        </h3>
        <p className="coach-why">{feedback.move.why}</p>
        {feedback.move.mistakes?.map((m) => (
          <MistakeExplorer
            key={m.san}
            prefix={prefix}
            mistake={m}
            orientation={orientation}
          />
        ))}
        <p className="muted">
          Play it on the board, or press <kbd>→</kbd> for Next.
        </p>
      </div>
    );
  }

  if (feedback.kind === 'wrong') {
    return (
      <div className="coach coach--wrong">
        <h3 className="coach-title">Not this time</h3>
        <p>
          <strong>{feedback.played}</strong> is legal, but the line plays{' '}
          <strong>{feedback.expectedSan}</strong>.
        </p>
        <p className="coach-why">{feedback.expected.why}</p>
        {feedback.expected.mistakes?.map((m) => (
          <MistakeExplorer
            key={m.san}
            prefix={prefix}
            mistake={m}
            orientation={orientation}
          />
        ))}
        <p className="muted">Play the highlighted move to continue.</p>
      </div>
    );
  }

  if (feedback.kind === 'revealed') {
    return (
      <div className="coach coach--hint">
        <h3 className="coach-title">The move is {feedback.expectedSan}</h3>
        <p className="coach-why">{feedback.expected.why}</p>
        <p className="muted">Play it on the board to carry on.</p>
      </div>
    );
  }

  if (feedback.kind === 'correct') {
    return (
      <div className="coach coach--correct">
        <h3 className="coach-title">
          {feedback.san}
          {feedback.move.critical && <span className="coach-key">key move</span>}
        </h3>
        <p className="coach-why">{feedback.move.why}</p>
        {trainer.waitingForOpponent && <p className="muted">Waiting for the reply…</p>}
      </div>
    );
  }

  return (
    <div className="coach">
      <h3 className="coach-title">{line.name}</h3>
      <p className="muted">
        {trainer.mode === 'study'
          ? 'You will be shown each move and the reason for it before you play. Nothing here is scored.'
          : trainer.isTraineeTurn
            ? 'Play the move the line calls for. If you are not sure, use “Show me” — being told is better than guessing.'
            : 'Watch the reply, then find your move.'}
      </p>
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <>
      <SiteHeader />
      <main className="page page--wide page--study">{children}</main>
    </>
  );
}
